import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { Box, Typography, Divider, Paper, Grid, Card, CardContent, CardActions, Button, TextField, Chip } from '@mui/material';
import { useSnackbar } from 'notistack';
import LibrarySelect from 'components/librarySelect';
import { useSearchParams } from 'react-router-dom';
import { parse } from 'date-fns';
import MobileDateRangePicker from '@mui/lab/MobileDateRangePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { getConstDbFormat } from 'utils/common';
import { getBookingsByDueDate } from 'services/bookings';
import AmountDialog from './AmountDialog';
import ScrollDialog from 'components/ScrollDialog';
import { formatTime } from 'services/commonServices';

const Dues = () => {

  const [searchParams] = useSearchParams();
  const defaultFilterDates = useMemo(() => {
    if( searchParams ){
      const fromString = searchParams.get('from'), toString = searchParams.get('to');
      let from, to;
      if(fromString && toString){
        try{
          from = parse(fromString, 'yyyy-MM-dd', new Date());
          to = parse(toString, 'yyyy-MM-dd', new Date());
          return [to,from]
        }catch(e){
        }
      }
    }
    return [new Date(), new Date()];
  }, [searchParams]);
  const [ filterDates, setFilterDates] = useState([...defaultFilterDates]);
  const [openModal, setOpenModal] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [dues, setDues] = useState([]);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [librarySelected, setLibrarySelected] = useState({});

  const onLibrarySelected = useCallback((lib) => {
    setLibrarySelected(lib);
  },[]);

  useEffect(() => {
    const [fromDate,  toDate] = filterDates;
    if(fromDate && toDate && librarySelected?.libraryId){
      getBookingsDueData();
    }
  }, [filterDates, librarySelected]);

  function onDateAccepted(data) {
    const [fromDate,  toDate] = data;
    if((fromDate && toDate)) {
      setFilterDates([...data]);
    }else{
      setFilterDates(null);
      enqueueSnackbar(`Select ${fromDate === null ? 'From Date' : 'To Date'}`, {variant: 'error'});
    }
  }

  const onModalClose = useCallback(
    () => {
      setOpenModal(false);
      setBookingDetails({})
    },
    [setOpenModal],
  )

  function getBookingsDueData() {
    const [fromDate,  toDate] = filterDates;
    const reqData = {
      fromDate: getConstDbFormat(fromDate),
      toDate: getConstDbFormat(toDate),
      propertyId: librarySelected?.libraryId,
      propertyType: 'LIBRARY'
    };

    getBookingsByDueDate({...reqData}).then(({response, error}) => {
      if(response){
        setDues([...response]);
      } else {
        setDues([]);
        enqueueSnackbar((response?.message || error), {variant: 'error'});
      }
    });
  }

  const onCollectButtonClick = (e, booking) => {
    setBookingDetails({...booking});
    setOpenModal(true);
  }
  
  const onAmountUpdate = (success) => {
    if(success){
      setOpenModal(false)
      getBookingsDueData();
    }
  }
  

  return (
    <Paper sx={{ minWidth: 275, py: 2, px: 3, minHeight: "calc(100vh - 100px )" }}>
      <Box noValidate  sx={{ mt: 2 }} >
        <Grid container columnSpacing={4} rowSpacing={4} >
          <Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
            <Typography component="h2" variant="h6" color="typography.heading"  gutterBottom sx={{mb: 1}}>
              Dues
            </Typography>
          </Grid>
         
        </Grid>
        <Divider />
        <Grid container columnSpacing={4} rowSpacing={4} sx={{mt: 1 }}  justifyContent={{ xs:"center", md:"flex-start"}} >
          <Grid container item xs={12} sm={12} md={12} xl={12} lg={12} sx={{ p: '0.8rem !important', ml:4, bgcolor: "#f3f3f3"}}>
            <Grid item xs={12} sm={12} md={12} xl={12} lg={12}  >
              <Typography variant="subtitle2"  component="div">
              Filters
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={6} xl={6} lg={6}  sx={{mt: 1.4}}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <MobileDateRangePicker
                  startText="From Date"
                  endText="To Date"
                  value={filterDates}
                  // onError = {onDateError}
                  inputFormat="dd/MM/yyyy"
                  // clearable={true}
                  onChange={(newValue) => {
                    return newValue;
                  }}
                  onAccept={(data) => onDateAccepted(data)}
                  renderInput={(startProps, endProps) => (
                    <React.Fragment>
                      <TextField {...startProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}}/>
                      <Box sx={{ mx: 2 }}> to </Box>
                      <TextField {...endProps} size="small" onKeyDown={(e) => {e.preventDefault();return false}} />
                    </React.Fragment>
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={12} md={6} xl={6} lg={6}  sx={{mt: 1.4, display: 'flex',justifyContent: {md: 'end'}}}>
              <LibrarySelect variant="outlined" onLibrarySelected={onLibrarySelected} defaultSelectId={searchParams?.get('libraryId')} />
            </Grid>
          </Grid>
          
          {dues.map((booking, index) =>(
            <Grid item xs={12} sm={10} md={6} xl={4} lg={4} key={index}>
              <Card sx={{  backgroundColor: '#f8f8f8' }}>
                <CardContent sx={{py: 1}}>
                  <Typography noWrap variant="h6"  >
                    {booking?.user?.displayName}
                  </Typography>
                  <Typography gutterBottom color="text.secondary"  variant="caption" component="div" noWrap >
                    {booking?.library?.name}
                  </Typography>
                  <Typography  variant="caption" component="div" noWrap>
                    {booking?.seatCount} Seat: {booking?.seatIds.join(', ')}
                  </Typography>
                  <Typography  gutterBottom variant="caption" component="div" noWrap>
                    <strong>{formatTime(booking?.fromTime)} - {formatTime(booking?.toTime)}</strong>
                  </Typography>
                  <Typography gutterBottom variant="subtitle2" component="div" noWrap color="error">
                    Amount Due: Rs.{booking?.bookingTransaction?.amountDue}
                  </Typography>
                </CardContent>
                <CardActions sx={{justifyContent: 'space-between',}}>
                  <Chip label={`Due on: ${booking?.bookingTransaction?.dueDate}`} color="warning" />
                  <Button size="small" color="info"  sx={{textTransform: 'none'}} onClick={(e) => onCollectButtonClick(e, booking)}>
                    Collect Dues
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
      <ScrollDialog
        title="Collect Dues"
        onModalClose={onModalClose}
        open={openModal}
        showCloseIcon={true}
        maxWidth="xs"
        disableDialogActions={true}
      >
        <AmountDialog onAmountUpdate={onAmountUpdate} bookingDetails={bookingDetails} />
      </ScrollDialog>
    </Paper>
  );
}
 
export default Dues;
